import React, { ReactElement } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import CompanySettings from './menus/CompanySettings';
import UsersSettings from './menus/users/UsersListSettings';
import history from 'helpers/history';
import LabelSettings from './menus/labels/LabelSettings';
import SubscriptionSettings from './menus/SubscriptionSettings';
import WorkflowSettings from './menus/WorkflowSettings';
import InspectionSettings from './menus/InspectionSettings';
import DeletedItemsPage from './menus/deletedItems.tsx/DeletedItemsPage';
import TaxSettings from './menus/TaxSettings';
import LaborRateSettings from './menus/LaborRatesSettings';
import FeesSettings from './menus/FeesSettings';
import PartsMatricesSettings from './menus/matrices/PartsMatricesSettings';
import CannedJobSettingsPage from './cannedjobs/CannedJobsSettingsPage';
import RequiredCustomerInfoSettings from './menus/RequiredCustomerInfoSettings';
import AppointmentNoShowSettings from './menus/AppointmentNoShowSettings';

import {
    Building2,
    Calculator,
    Briefcase,
    DollarSign,
    Users,
    GitBranch,
    ClipboardCheck,
    Tag,
    Trash2,
    CreditCard,
    UserPlus,
    Menu,
    X,
    Settings as SettingsIcon,
    FileCheck2,
    CheckIcon,
    CheckSquare,
    UserX,
    Clock,
} from 'lucide-react';
import ROPostingRulesSettings from './menus/ROPostingRulesSettings';
import ROAgingRulesSettings from './menus/ROAgingRulesSettings';

interface ComponentState {
    activeMenuIndex?: number;
    isMobileSidebarOpen: boolean;
}

interface Props {
    dispatch: Dispatch;
}

class SettingsPage extends React.Component<Props, ComponentState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            activeMenuIndex: undefined,
            isMobileSidebarOpen: false,
        };
    }

    componentDidMount() {
        //@ts-ignore
        const { subpageName } = this.props.match.params;
        this.setActiveMenu(subpageName);
    }
    setActiveMenu(subpageName: string) {
        let activeMenuIndex = navigationItems.findIndex((menuItem) => menuItem.location === subpageName);
        if (activeMenuIndex === -1) activeMenuIndex = 0;
        this.setState({ activeMenuIndex });
    }

    toggleMobileSidebar = () => {
        this.setState({ isMobileSidebarOpen: !this.state.isMobileSidebarOpen });
    };

    closeMobileSidebar = () => {
        this.setState({ isMobileSidebarOpen: false });
    };

    handleMenuItemClick = (location: string) => {
        history.push(`/settings/${location}`);
        this.setState({
            activeMenuIndex: navigationItems.findIndex((navItem) => navItem.location === location),
            isMobileSidebarOpen: false, // Close mobile sidebar when item is selected
        });
    };
    render() {
        const { activeMenuIndex, isMobileSidebarOpen } = this.state;
        const currentPage = activeMenuIndex !== undefined ? navigationItems[activeMenuIndex] : null;
        return (
            <div className="flex bg-gray-50">
                {/* Mobile Overlay */}
                {isMobileSidebarOpen && (
                    <div
                        className="lg:hidden fixed inset-0 top-16 z-30 bg-black bg-opacity-50"
                        onClick={this.closeMobileSidebar}
                    />
                )}
                {/* Sidebar */}
                <div
                    className={`
                    fixed h-screen  overflow-y-auto lg:static lg:inset-y-0 top-16 lg:top-0 bottom-0 left-0 z-40
                    w-80 lg:w-60 
                    bg-white 
                    transform transition-transform duration-300 ease-in-out
                    lg:transform-none
                    ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    overflow-y-auto
                `}
                >
                    {/* Sidebar Header */}
                    <div className="fixed  w-80 lg:w-60  bg-white  h-screen border-r border-gray-200 ">
                        <div className="p-6 border-b border-gray-200 lg:border-b-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <SettingsIcon className="h-6 w-6 text-blue-600" />
                                    <h1 className="text-xl font-bold text-gray-900">Settings</h1>
                                </div>
                                <button
                                    onClick={this.closeMobileSidebar}
                                    className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <X className="h-5 w-5 text-gray-600" />
                                </button>
                            </div>
                        </div>

                        {/* Navigation */}
                        <nav className="p-6 space-y-8">
                            {navigationGroups.map((settingsGroup) => (
                                <div key={settingsGroup.groupName}>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                        {settingsGroup.groupName}
                                    </h3>
                                    <ul className="space-y-1">
                                        {settingsGroup.groupItems.map((item) => {
                                            const Icon = item.icon;
                                            const isActive = item.location === (currentPage?.location || '');
                                            return (
                                                <li key={item.name}>
                                                    <button
                                                        onClick={() => this.handleMenuItemClick(item.location)}
                                                        className={`w-full flex items-center px-3 py-2.5 text-sm font-semibold rounded-lg transition-colors text-left ${
                                                            isActive
                                                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                                        }`}
                                                    >
                                                        <Icon
                                                            className={`mr-3 h-5 w-5 flex-shrink-0 ${
                                                                isActive ? 'text-blue-500' : 'text-gray-400'
                                                            }`}
                                                        />
                                                        <span className="truncate">{item.name}</span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </nav>
                    </div>
                </div>{' '}
                {/* Main Content */}
                {currentPage && (
                    <div className="flex-1 flex flex-col lg:ml-0 lg:pt-0 min-w-0 overflow-hidden">
                        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={this.toggleMobileSidebar}
                                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <Menu className="h-6 w-6 text-gray-600" />
                                    </button>
                                    <div className="flex items-center space-x-2">
                                        <SettingsIcon className="h-6 w-6 text-blue-600" />
                                        <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
                                    </div>
                                </div>
                                {currentPage && (
                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                        <currentPage.icon className="h-4 w-4" />
                                        <span className="truncate max-w-32">{currentPage.name}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto pb-96 ">{currentPage.component}</div>
                    </div>
                )}
            </div>
        );
    }
}

const navigationItems: { name: string; location: string; icon: any; component?: React.ReactElement }[] = [
    {
        name: 'Company settings ',
        location: 'companySettings',
        icon: Building2,
        component: <CompanySettings />,
    },
    {
        name: 'Tax settings ',
        location: 'taxSettings',
        icon: Calculator,
        component: <TaxSettings />,
    },
    {
        name: 'Canned Jobs ',
        location: 'cannedJobs',
        icon: Briefcase,
        component: <CannedJobSettingsPage />,
    },
    {
        name: 'Labor Rates',
        location: 'laborRates',
        icon: DollarSign,
        component: <LaborRateSettings />,
    },
    {
        name: 'Pricing Matrices',
        location: 'pricingMatrices',
        icon: DollarSign,
        component: <PartsMatricesSettings />,
    },
    {
        name: 'Labor Matrices',
        location: 'laborMatrices',
        icon: DollarSign,
        component: <LaborRateSettings />,
    },
    {
        name: 'Fees',
        location: 'fees',
        icon: DollarSign,
        component: <FeesSettings />,
    },
    {
        name: 'Users',
        location: 'users',
        icon: Users,
        component: <UsersSettings />,
    },
    {
        name: 'Deleted items',
        location: 'deletedItems',
        icon: Trash2,
        component: <DeletedItemsPage />,
    },
    {
        name: 'Workflow',
        location: 'workflow',
        icon: GitBranch,
        component: <WorkflowSettings />,
    },
    {
        name: 'Inspections',
        location: 'inspection',
        icon: ClipboardCheck,
        component: <InspectionSettings />,
    },
    {
        name: 'Labels',
        location: 'labels',
        icon: Tag,
        component: <LabelSettings />,
    },
    {
        name: 'Required Customer Information',
        location: 'requiredCustomerInformation',
        icon: UserPlus,
        component: <RequiredCustomerInfoSettings />,
    },
    {
        name: 'Subscription',
        location: 'subscriptionSettings',
        icon: CreditCard,
        component: <SubscriptionSettings />,
    },
    {
        name: 'RO Posting Rules',
        location: 'roPostingRules',
        icon: CheckSquare,
        component: <ROPostingRulesSettings />,
    },
    {
        name: 'Appointment No-Show Rules',
        location: 'appointmentNoShowRules',
        icon: UserX,
        component: <AppointmentNoShowSettings />,
    },
    {
        name: 'Long Running RO Tracking',
        location: 'roAgingRules',
        icon: Clock,
        component: <ROAgingRulesSettings />,
    },
];

const navigationGroups: {
    groupName: string;
    groupItems: { name: string; location: string; icon: any; component?: React.ReactElement }[];
}[] = [
    {
        groupName: 'General Settings',
        groupItems: [
            navigationItems[0], // Company settings
            navigationItems[1], // Tax settings
        ],
    },
    {
        groupName: 'Operations',
        groupItems: [
            navigationItems[2], // Canned Jobs
            navigationItems[9], // Workflow
            navigationItems[3], // Labor Rates
            navigationItems[4], // Pricing Matrices
            navigationItems[5], // Labor Matrices
            navigationItems[10], // Inspections,
            navigationItems[14], //RO Posting Rules
            navigationItems[15], //Appointment No-Show Rules
            navigationItems[16], //RO Aging Rules
        ],
    },

    {
        groupName: 'System',
        groupItems: [
            navigationItems[6], // Fees
            navigationItems[7], // Users
            navigationItems[12], // Required Customer Information
            navigationItems[13], // Subscription settings
            navigationItems[11], // Labels
        ],
    },
    {
        groupName: 'Maintenance',
        groupItems: [
            navigationItems[8], // Deleted items
        ],
    },
];

export default connect()(SettingsPage);
